import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Mail, User, Lock, LogIn, UserPlus, MailCheck, KeyRound, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { authApi } from '@/services/api';
import { PasswordRequirements } from '@/components/PasswordRequirements';
import { isPasswordValid, passwordsMatch } from '@/lib/passwordPolicy';
import { toast } from 'sonner';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Mode = 'login' | 'signup' | 'forgot';

function extractError(err: unknown): string {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
      ?.message ||
    (err as Error)?.message ||
    'Une erreur est survenue';
  return Array.isArray(msg) ? msg.join(', ') : msg;
}

export function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const { login, signup, applySession, verifyAdminOtp } = useAuthStore();
  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [processing, setProcessing] = useState(false);
  // Écran de confirmation générique (email envoyé) : vérification ou reset.
  const [sentInfo, setSentInfo] = useState<{ kind: 'verify' | 'reset'; email: string } | null>(null);
  // Défi OTP (2ème facteur) affiché après un login Admin validé par mot de passe.
  const [otpChallenge, setOtpChallenge] = useState<{ otpToken: string; message: string } | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  if (!isOpen) return null;

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode);
    setPassword('');
    setConfirmPassword('');
    setCode('');
    setSentInfo(null);
    setOtpChallenge(null);
    setAcceptedTerms(false);
  };

  const resetAndClose = () => {
    setSentInfo(null);
    setOtpChallenge(null);
    onClose();
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setProcessing(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSentInfo({ kind: 'reset', email: email.trim() });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    if (mode === 'signup' && !name.trim()) return;

    if (mode === 'signup') {
      if (!isPasswordValid(password)) {
        toast.error('Le mot de passe ne respecte pas les exigences de sécurité.');
        return;
      }
      if (!passwordsMatch(password, confirmPassword)) {
        toast.error('Les mots de passe ne correspondent pas.');
        return;
      }
    }

    setProcessing(true);
    try {
      if (mode === 'signup') {
        const { requiresVerification } = await signup(
          name.trim(),
          email.trim(),
          password,
          confirmPassword,
        );
        setPassword('');
        setConfirmPassword('');
        if (requiresVerification) {
          // On ne connecte pas : l'utilisateur doit confirmer son email.
          setSentInfo({ kind: 'verify', email: email.trim() });
          return;
        }
        toast.success('Compte créé avec succès !');
      } else {
        const outcome = await login(email.trim(), password);
        setPassword('');
        if (outcome.requiresOtp) {
          // Compte Admin : mot de passe validé, code OTP envoyé par email.
          setOtpChallenge({ otpToken: outcome.otpToken, message: outcome.message });
          return;
        }
        toast.success('Connecté avec succès !');
      }
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpChallenge || code.trim().length !== 6) return;
    setProcessing(true);
    try {
      await verifyAdminOtp(otpChallenge.otpToken, code.trim());
      toast.success('Connecté avec succès !');
      setOtpChallenge(null);
      setCode('');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleResend = async () => {
    if (!sentInfo) return;
    setProcessing(true);
    try {
      await authApi.resendVerification(sentInfo.email);
      toast.success('Nouveau code envoyé.');
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sentInfo || code.trim().length !== 6) return;
    setProcessing(true);
    try {
      const { data } = await authApi.verifyEmail(sentInfo.email, code.trim());
      applySession(
        data.access_token,
        data.user as { name: string; email: string; role?: 'user' | 'admin' },
      );
      toast.success('Compte activé !');
      setSentInfo(null);
      setCode('');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const signupReady =
    mode !== 'signup' ||
    (name.trim().length > 0 &&
      email.trim().length > 0 &&
      isPasswordValid(password) &&
      passwordsMatch(password, confirmPassword) &&
      acceptedTerms);

  const headerIcon = otpChallenge ? (
    <KeyRound className="w-7 h-7 text-fg" />
  ) : sentInfo ? (
    <MailCheck className="w-7 h-7 text-fg" />
  ) : mode === 'signup' ? (
    <UserPlus className="w-7 h-7 text-fg" />
  ) : mode === 'forgot' ? (
    <KeyRound className="w-7 h-7 text-fg" />
  ) : (
    <LogIn className="w-7 h-7 text-fg" />
  );

  const title = otpChallenge
    ? 'Vérification en deux étapes'
    : sentInfo
      ? 'Vérifiez votre boîte mail'
      : mode === 'signup'
        ? 'Créer un compte'
        : mode === 'forgot'
          ? 'Mot de passe oublié'
          : 'Se connecter';

  const subtitle = otpChallenge
    ? otpChallenge.message
    : sentInfo
      ? sentInfo.kind === 'verify'
        ? `Nous avons envoyé un code à 6 chiffres à ${sentInfo.email}. Saisissez-le ci-dessous pour activer votre compte.`
        : `Si un compte existe pour ${sentInfo.email}, vous allez recevoir un lien de réinitialisation.`
      : mode === 'signup'
        ? 'Créez un compte pour sauvegarder votre progression'
        : mode === 'forgot'
          ? 'Entrez votre email : nous vous enverrons un lien de réinitialisation.'
          : 'Connectez-vous pour retrouver votre progression';

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={resetAndClose} />

      <div className="relative w-full max-w-md mx-auto bg-app border border-line rounded-t-3xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <button onClick={resetAndClose} className="absolute top-4 right-4 text-fg-faint hover:text-fg-muted transition-colors z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 sm:px-6 pt-6 sm:pt-8 pb-4 text-center">
          <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            {headerIcon}
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-fg mb-2">{title}</h2>
          <p className="text-xs sm:text-sm text-fg-subtle leading-relaxed">{subtitle}</p>
        </div>

        {/* Défi OTP admin (2ème facteur) : saisie du code reçu par email */}
        {otpChallenge ? (
          <div className="px-5 sm:px-6 pb-6 space-y-3">
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                autoFocus
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-center text-2xl font-semibold tracking-[0.4em]"
              />
              <button
                type="submit"
                disabled={processing || code.length !== 6}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                {processing ? 'Vérification...' : 'Vérifier le code'}
              </button>
            </form>
            <button
              onClick={() => switchMode('login')}
              className="w-full flex items-center justify-center gap-2 text-xs text-fg-faint hover:text-fg-subtle transition-colors py-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
            </button>
          </div>
        ) : /* Écran de confirmation (email envoyé) */
        sentInfo ? (
          <div className="px-5 sm:px-6 pb-6 space-y-3">
            {sentInfo.kind === 'verify' && (
              <>
                <form onSubmit={handleVerify} className="space-y-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="\d{6}"
                    maxLength={6}
                    autoFocus
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-center text-2xl font-semibold tracking-[0.4em]"
                  />
                  <button
                    type="submit"
                    disabled={processing || code.length !== 6}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
                  >
                    {processing ? 'Vérification...' : 'Activer mon compte'}
                  </button>
                </form>
                <button
                  onClick={handleResend}
                  disabled={processing}
                  className="w-full py-3 bg-overlay-1 border border-line rounded-xl text-sm text-fg-muted hover:bg-overlay-2 transition-colors disabled:opacity-60"
                >
                  {processing ? 'Envoi...' : "Je n'ai rien reçu — renvoyer le code"}
                </button>
              </>
            )}
            <button
              onClick={() => switchMode('login')}
              className="w-full flex items-center justify-center gap-2 text-xs text-fg-faint hover:text-fg-subtle transition-colors py-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
            </button>
          </div>
        ) : mode === 'forgot' ? (
          /* Formulaire mot de passe oublié */
          <form onSubmit={handleForgot} className="px-5 sm:px-6 pb-4 space-y-3">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
              <input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={processing || !email.trim()}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {processing ? 'Envoi...' : 'Envoyer le lien'}
            </button>
            <button
              type="button"
              onClick={() => switchMode('login')}
              className="w-full flex items-center justify-center gap-2 text-xs text-fg-faint hover:text-fg-subtle transition-colors py-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
            </button>
          </form>
        ) : (
          /* Formulaire login / signup */
          <>
            <form onSubmit={handleSubmit} className="px-5 sm:px-6 pb-4 space-y-3">
              {mode === 'signup' && (
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                  <input
                    type="text"
                    placeholder="Votre nom"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
                  />
                </div>
              )}
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                <input
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={mode === 'signup' ? 8 : 1}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
                />
              </div>

              {mode === 'login' && (
                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    onClick={() => switchMode('forgot')}
                    className="text-xs text-fg-faint hover:text-green-400 transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              )}

              {mode === 'signup' && (
                <>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-faint" />
                    <input
                      type="password"
                      placeholder="Confirmer le mot de passe"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={8}
                      autoComplete="new-password"
                      className="w-full pl-10 pr-4 py-3 bg-overlay-1 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
                    />
                  </div>
                  <PasswordRequirements
                    password={password}
                    confirmPassword={confirmPassword}
                    showMatch
                  />
                  <label className="flex items-start gap-2 text-[11px] text-fg-subtle leading-relaxed">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span>
                      J’accepte les{' '}
                      <Link to="/legal/cgu" onClick={resetAndClose} className="text-green-400 underline underline-offset-2">
                        CGU
                      </Link>
                      {' '}et la{' '}
                      <Link to="/legal/privacy" onClick={resetAndClose} className="text-green-400 underline underline-offset-2">
                        politique de confidentialité
                      </Link>
                      .
                    </span>
                  </label>
                </>
              )}

              <button
                type="submit"
                disabled={processing || !signupReady}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-line border-t-white rounded-full animate-spin" />
                    Chargement...
                  </span>
                ) : mode === 'signup' ? "S'inscrire" : 'Se connecter'}
              </button>
            </form>

            <div className="px-5 sm:px-6 pt-2 pb-6 text-center">
              <button
                onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}
                className="text-xs text-fg-faint hover:text-fg-subtle transition-colors"
              >
                {mode === 'signup' ? (
                  <>Déjà un compte ? <span className="text-green-500 font-semibold hover:text-green-400">Se connecter</span></>
                ) : (
                  <>Pas de compte ? <span className="text-green-500 font-semibold hover:text-green-400">S'inscrire</span></>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
